import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Trophy, Shield, TrendingUp, Award, ArrowRight, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { formatNumber } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Scoring weight constants (must match backend scoring_engine.py)
const TIPPER_WEIGHT = 0.75;
const CREATOR_WEIGHT = 0.25;

export const HomePage = () => {
  const [stats, setStats] = useState(null);
  const [currentEpoch, setCurrentEpoch] = useState(null);
  
  useEffect(() => {
    loadStats();
  }, []);
  
  const loadStats = async () => {
    try {
      const epochRes = await axios.get(`${BACKEND_URL}/api/epochs/current`);
      setCurrentEpoch(epochRes.data);
      setStats({
        totalParticipants: epochRes.data.totalParticipants || 0,
        rewardPool: epochRes.data.totalRewardPool || '0'
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };
  
  return (
    <div className="min-h-screen bg-[#d4cfc4]" data-testid="home-page">
      {/* Hero Section */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/80 rounded-full px-4 py-2 mb-6">
              <span className="w-2 h-2 bg-[#f0b90b] rounded-full"></span>
              <span className="text-sm font-medium text-[#333]">TRUE POINTS</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-[#1a1a1a]">
              Tip Creators,<br />
              <span className="text-[#f0b90b]">Earn Rewards</span>
            </h1>
            
            <p className="text-lg text-[#666] mb-8 max-w-xl mx-auto">
              Secure, epoch-based reward distribution powered by Merkle proofs on BNB Chain.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-10">
              <Link to="/verify">
                <Button className="bg-[#f0b90b] hover:bg-[#d4a50a] text-white px-8 py-6 text-lg rounded-xl">
                  Get Verified
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/rewards">
                <Button variant="outline" className="border-[#ccc] bg-white hover:bg-[#f5f5f5] text-[#333] px-8 py-6 text-lg rounded-xl">
                  View Rewards
                </Button>
              </Link>
            </div>
            
            {currentEpoch && currentEpoch.epochId && (
              <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/80 border border-[#e0dbd3]">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-sm text-[#333]">
                  Epoch {currentEpoch.epochId} • {formatNumber(currentEpoch.totalRewardPool || stats?.rewardPool || 0)} BNB Pool
                </span>
              </div>
            )}
          </div>
        </div>
      </section>
      
      {/* Features */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-10 text-[#1a1a1a]">How It Works</h2>
            
            <div className="grid md:grid-cols-3 gap-5">
              <div className="bg-[#ebe6de] rounded-2xl p-6">
                <div className="w-12 h-12 bg-[#f0b90b] rounded-xl flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-[#1a1a1a]">1. Get Verified</h3>
                <p className="text-sm text-[#666] mb-4">
                  Link your social account and complete verification to become eligible for rewards.
                </p>
                <ul className="space-y-2 text-sm text-[#888]">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Submit social handle
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Post verification code
                  </li>
                </ul>
              </div>
              
              <div className="bg-[#ebe6de] rounded-2xl p-6">
                <div className="w-12 h-12 bg-[#f0b90b] rounded-xl flex items-center justify-center mb-4">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-[#1a1a1a]">2. Tip & Earn</h3>
                <p className="text-sm text-[#666] mb-4">
                  Send tips to creators. Earn TRUE Points for both tipping and receiving.
                </p>
                <ul className="space-y-2 text-sm text-[#888]">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#f0b90b]" />
                    {Math.round(TIPPER_WEIGHT * 100)}% to tippers
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#f0b90b]" />
                    {Math.round(CREATOR_WEIGHT * 100)}% to creators
                  </li>
                </ul>
              </div>
              
              <div className="bg-[#ebe6de] rounded-2xl p-6">
                <div className="w-12 h-12 bg-[#f0b90b] rounded-xl flex items-center justify-center mb-4">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-[#1a1a1a]">3. Claim Rewards</h3>
                <p className="text-sm text-[#666] mb-4">
                  After each 7-day epoch, claim your TIP tokens directly to your wallet.
                </p>
                <ul className="space-y-2 text-sm text-[#888]">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Merkle proofs
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    5-day claim window
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Why TRUE Points */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-center mb-8 text-[#1a1a1a]">Why TRUE Points?</h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { title: 'Secure by Design', desc: 'Immutable contracts with no admin functions.' },
                { title: 'Anti-Sybil', desc: 'Advanced detection and manual verification.' },
                { title: 'Fair Distribution', desc: 'Quadratic rewards with caps prevent whale dominance.' },
                { title: 'Transparent', desc: 'All snapshots published to IPFS.' }
              ].map((item) => (
                <div key={item.title} className="bg-white/60 rounded-xl p-5 border border-[#e0dbd3]">
                  <h3 className="font-semibold mb-1 text-[#1a1a1a] flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    {item.title}
                  </h3>
                  <p className="text-sm text-[#666]">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center bg-[#1a1a1a] rounded-2xl p-10">
            <Trophy className="w-12 h-12 mx-auto mb-4 text-[#f0b90b]" />
            <h2 className="text-2xl font-bold mb-3 text-white">Ready to Earn?</h2>
            <p className="text-[#888] mb-6">
              Join the TRUE Points ecosystem today.
            </p>
            <Link to="/verify">
              <Button className="bg-[#f0b90b] hover:bg-[#d4a50a] text-white px-8 py-3 rounded-xl">
                Get Started
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
